import { createPortal } from 'react-dom';
import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';
import { useImageDecodeFallback } from 'src/lib/useImageDecodeFallback';

interface ImageModalProps {
  open: boolean;
  url: string;
  filename: string;
  onClose: () => void;
  /** Document Hub document ULID — when set, a link to the hub is shown. */
  documentId?: string;
  /**
   * A browser-renderable image tried only when `url` fails to decode (e.g. a
   * HEIC original in a browser without native support). Support is detected by
   * the decode itself (EAFP).
   */
  fallbackUrl?: string;
}

export default function ImageModal({ open, url, filename, onClose, documentId, fallbackUrl }: ImageModalProps) {
  const { tenantCode } = useParams<{ tenantCode: string }>();

  const { effectiveSrc, onError, onLoadCheck } = useImageDecodeFallback(url, fallbackUrl);

  if (!open) return null;

  const hubUrl = documentId && tenantCode ? `/${tenantCode}/wiki/${documentId}` : null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70" onClick={onClose}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-10"
        aria-label="Close image"
      >
        <XIcon size={24} weight="bold" />
      </button>

      <div
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={effectiveSrc}
          alt={filename}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onError={onError}
          onLoad={(e) => {
            onLoadCheck(e.currentTarget);
          }}
        />
        <div className="flex items-center gap-3">
          {filename && <div className="text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded">{filename}</div>}
          {hubUrl && (
            <a
              href={hubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-white text-sm bg-black bg-opacity-50 hover:bg-opacity-70 px-4 py-2 rounded transition-colors"
            >
              <ArrowSquareOutIcon size={15} />
              View in Document Hub
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
